import { WAITING_SUPPORT_COPY } from './waitingForSurgerySupport.js';

// An unlisted, informational preview. This link is not access control and
// unlisted content must not be treated as authorised for clinical deployment.
export const WAITING_FOR_SURGERY_ID = 'waiting-for-surgery';
export const WAITING_FOR_SURGERY_CONTENT_VERSION = '2026-10-17-support-v1';
export const WAITING_FOR_SURGERY_ROUTE = '/j/wfw-7Q2M9K4V6R8P';
export const WAITING_FOR_SURGERY_TEMPLATE = Object.freeze({
  id: WAITING_FOR_SURGERY_ID,
  category: 'waiting_well',
  title: 'While You Wait for Surgery',
  summary: 'A practical, accessible orientation guide to the waiting-list period, health preparation and where to seek advice if things change.',
  route: WAITING_FOR_SURGERY_ROUTE,
  unlisted: true,
  stages: [
    { id: 'waiting-list', title: "You're on a waiting list" },
    { id: 'looking-after-health', title: 'Looking after your health' },
    { id: 'health-changes', title: 'If your health changes' },
    { id: 'preassessment', title: 'Preparing for preassessment' },
    { id: 'operation-preparation', title: 'Getting ready for your operation' },
  ],
});
export const WAITING_FOR_SURGERY_SECTIONS = Object.freeze([
  {
    id: 'waiting-list',
    title: "You're on a waiting list",
    description: 'What to expect while waiting for planned surgery',
    body: [
      'Your hospital may contact you about an appointment, assessment or operation date. How and when this happens varies between services.',
      'Keep the contact details held by your hospital up to date and follow any letters or messages from your team.',
      'Your hospital can explain where to find official information about its waiting list. This guide does not show your own position or waiting time.',
    ],
  },
  {
    id: 'looking-after-health',
    title: 'Looking after your health',
    description: 'Finding trusted resources and local support',
    body: [
      'Waiting can be an opportunity to find out about available support with smoking, alcohol, nutrition, physical activity, sleep and wellbeing.',
      'Not every activity or resource will be suitable for everyone. Ask your healthcare team what is appropriate for your health and planned operation.',
      'Your hospital may offer a prehabilitation or health-optimisation service. The links below can direct you to approved information where available.',
    ],
  },
  {
    id: 'health-changes',
    title: 'If your health changes',
    description: 'How to seek advice while waiting',
    body: [
      'If your symptoms change or worsen, you develop new symptoms, or you are worried about your health, seek clinical advice rather than waiting until your operation.',
      'Follow the contact advice provided by your hospital team. If you need urgent help and are unsure where to go, NHS 111 can direct you to an appropriate service.',
      'For a life-threatening emergency, call 999.',
    ],
  },
  {
    id: 'preassessment',
    title: 'Preparing for preassessment',
    description: 'Understanding the appointment and preparing questions',
    body: [
      'Preassessment is a chance for the hospital team to learn about your health, medicines and support needs before surgery.',
      'Your hospital may ask you to bring a medicines list or other information. Follow the instructions sent by your own team.',
      'You can make a note of questions, concerns or reasonable adjustments you would like to discuss.',
    ],
  },
  {
    id: 'operation-preparation',
    title: 'Getting ready for your operation',
    description: 'Finding your own hospital instructions',
    body: [
      'Your hospital will provide instructions about preparing for surgery, which may include medicines, eating, drinking, travel or arrangements for going home.',
      'Follow the personalised instructions from your hospital, not general advice from this page.',
      'You can also explore Know Your Care’s existing Surgery or Day Surgery journey to understand some of the people and stages you may encounter.',
    ],
  },
]);
export const WAITING_FOR_SURGERY_LINKS = Object.freeze([
  { id: 'hospital-team', title: 'Contact your hospital team', description: 'Your hospital’s approved route for queries or changes in your health', category: 'safety' },
  { id: 'waiting-information', title: 'Official waiting-list information', description: 'Waiting-list updates and guidance from your NHS service', category: 'information' },
  { id: 'health-resources', title: 'Health and wellbeing resources', description: 'Approved advice and local support for preparing while you wait', category: 'information' },
  { id: 'prehabilitation', title: 'Local prehabilitation services', description: 'Support available through your hospital or local NHS services', category: 'information' },
  { id: 'preassessment', title: 'Preassessment information', description: 'Practical details about your preassessment service', category: 'information' },
  { id: 'surgery-instructions', title: 'Your hospital’s surgery information', description: 'Where to find your hospital’s approved guidance', category: 'information' },
]);
export function createWaitingForSurgeryJourney() {
  return {
    id: 'generic-waiting-for-surgery',
    templateId: WAITING_FOR_SURGERY_ID,
    accessKey: 'generic-waiting-for-surgery',
    status: 'draft',
    public: false,
    trustId: 'generic_nhs',
    trustName: 'Your Trust',
    hospitalName: 'Your hospital or service',
    title: WAITING_FOR_SURGERY_TEMPLATE.title,
    eyebrow: 'Unlisted Journey – review preview',
    introduction: WAITING_FOR_SURGERY_TEMPLATE.summary,
    roles: [],
    stages: WAITING_FOR_SURGERY_SECTIONS.map((stage) => ({ ...stage })),
    usefulLinks: WAITING_FOR_SURGERY_LINKS.map((link) => ({ ...link, url: null })),
    questions: [...WAITING_SUPPORT_COPY.questions],
    disclaimer: 'General information only. This website does not monitor you, assess symptoms, advise whether it is safe to wait, or decide whether you are fit for surgery. Your healthcare team remains responsible for personalised advice and clinical decisions.',
  };
}
